import {
  isSnapshotMediaAvailable,
  listIncidentsWithAvailableSnapshot,
} from '../continuousSafety/overviewConditionEvidenceThumb';
import type { Incident } from '../../services/incidentsApi';
import {
  SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
  SHOWCASE_ROOM_B_RAE_CONDITION_ID,
} from './showcasePresentation';

export const SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS = 3;
export const SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE = 'near_miss';
export const SHOWCASE_PROX_EVIDENCE_CAMERA_ID = 'cam_walkway_zone_demo';
export const SHOWCASE_PROX_EVIDENCE_CONDITION_ID = SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID;
export const SHOWCASE_RAE_EVIDENCE_INCIDENT_TYPE = 'zone_violation';
export const SHOWCASE_RAE_EVIDENCE_CAMERA_ID = 'cam_walkway_zone_demo';
export const SHOWCASE_RAE_EVIDENCE_ZONE_ID = 'worksafety';
export const SHOWCASE_RAE_EVIDENCE_CONDITION_ID = SHOWCASE_ROOM_B_RAE_CONDITION_ID;
export const SHOWCASE_RECENT_EVIDENCE_LIST_LIMIT = 16;
export const SHOWCASE_RECENT_EVIDENCE_LIST_PAGES = 4;
/** zone_violation lists on yyz can exceed Overview's 12s thumb budget. */
export const SHOWCASE_RECENT_EVIDENCE_LIST_TIMEOUT_MS = 45_000;

export type ShowcaseRecentEvidenceStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type ShowcaseRecentEvidenceItem = {
  incidentId: string;
  timestamp: string | null;
  imageUrl: string;
  status: 'ready';
};

export type ShowcaseRecentEvidenceState = {
  status: ShowcaseRecentEvidenceStatus;
  items: ShowcaseRecentEvidenceItem[];
};

export type ShowcaseRecentEvidenceViewItem = {
  imageUrl: string;
  timeLabel: string;
};

export type ShowcaseRecentEvidenceView = {
  status: ShowcaseRecentEvidenceStatus;
  items: ShowcaseRecentEvidenceViewItem[];
};

export function incidentStoredConditionId(incident: Incident | null | undefined): string {
  if (!incident) return '';
  const envelope = incident.envelope_metadata;
  const fromEnvelope = envelope && typeof envelope === 'object'
    ? String((envelope as { condition_id?: unknown }).condition_id || '').trim()
    : '';
  const fromRow = String((incident as { condition_id?: unknown }).condition_id || '').trim();
  return fromEnvelope || fromRow;
}

/**
 * Half-open [start, end) on the evidence timestamp. Missing time is outside
 * the interval so an undated row cannot stand in for peak evidence.
 */
export function incidentWithinEvidenceInterval(
  incident: Incident | null | undefined,
  interval: { start?: string | null; end?: string | null } | null | undefined,
): boolean {
  const start = Date.parse(String(interval?.start || ''));
  const end = Date.parse(String(interval?.end || ''));
  const ts = Date.parse(incidentEvidenceTimestamp(incident) || '');
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(ts)) return false;
  return ts >= start && ts < end;
}

/**
 * Exclude another condition or policy only when that identity is stored.
 * A blank stored value means the dimension is not available on the row.
 */
export function incidentMatchesBoundIdentity(
  incident: Incident | null | undefined,
  bound: { conditionId?: string | null; policyId?: string | null },
): boolean {
  if (!incident) return false;
  const wantedCondition = String(bound.conditionId || '').trim();
  const storedCondition = incidentStoredConditionId(incident);
  if (wantedCondition && storedCondition && storedCondition !== wantedCondition) return false;
  const wantedPolicy = String(bound.policyId || '').trim();
  const storedPolicy = String(incident.policy_id || '').trim();
  if (wantedPolicy && storedPolicy && storedPolicy !== wantedPolicy) return false;
  return true;
}

/**
 * Same occurrence instant the incidents list filter uses:
 * COALESCE(ts_start, created_at). Snapshot captured_at and evidence_ts_ms
 * are not this clock, so a later still cannot render outside the peak.
 */
export function incidentEvidenceTimestamp(incident: Incident | null | undefined): string | null {
  if (!incident) return null;
  const text = String(incident.ts_start || incident.created_at || '').trim();
  return text || null;
}

/**
 * Newest-first READY/AVAILABLE stills. Unique incident_id. Skip identical
 * timestamps when they would look like the same moment. No physical-event
 * invention and no fixture stills.
 */
export function pickUniqueRecentEvidenceIncidents(
  incidents: Incident[] | null | undefined,
  prefer: {
    cameraId?: string | null;
    zoneId?: string | null;
    incidentType?: string | null;
    candidateIncidentTypes?: string[] | null;
  } = {},
  maxItems = SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS,
): Incident[] {
  const incidentType = String(prefer.incidentType || SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE).trim().toLowerCase()
    || SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE;
  const cameraId = String(prefer.cameraId || '').trim() || null;
  const zoneId = String(prefer.zoneId || '').trim() || null;
  const preferredTypes = [
    incidentType,
    ...(prefer.candidateIncidentTypes || []).map((value) => String(value || '').trim().toLowerCase()),
  ].filter(Boolean);
  const uniqueTypes = Array.from(new Set(preferredTypes));
  const available = listIncidentsWithAvailableSnapshot(incidents, {
    cameraId,
    zoneId,
    preferredIncidentTypes: uniqueTypes,
  }).filter((incident) => {
    if (!isSnapshotMediaAvailable(incident)) return false;
    const type = String(incident.incident_type || incident.title || '').trim().toLowerCase();
    return uniqueTypes.includes(type);
  });

  const ranked = [...available].sort((left, right) => {
    const leftTs = Date.parse(incidentEvidenceTimestamp(left) || '') || 0;
    const rightTs = Date.parse(incidentEvidenceTimestamp(right) || '') || 0;
    return rightTs - leftTs;
  });

  const picked: Incident[] = [];
  const seenIds = new Set<string>();
  const seenTimestamps = new Set<string>();
  for (const incident of ranked) {
    const id = String(incident.incident_id || '').trim();
    if (!id || seenIds.has(id)) continue;
    const timestamp = incidentEvidenceTimestamp(incident);
    if (timestamp && seenTimestamps.has(timestamp)) continue;
    seenIds.add(id);
    if (timestamp) seenTimestamps.add(timestamp);
    picked.push(incident);
    if (picked.length >= maxItems) break;
  }
  return picked;
}

export function presentShowcaseRecentEvidence(
  items: ShowcaseRecentEvidenceItem[],
  timeLabelFor: (timestamp: string | null) => string,
): ShowcaseRecentEvidenceViewItem[] {
  return items.slice(0, SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS).map((item) => ({
    imageUrl: item.imageUrl,
    timeLabel: timeLabelFor(item.timestamp),
  }));
}
